import { z } from 'zod';
import type { Dictionary } from '../i18n/registry.js';

/**
 * Клиентская валидация повторяет форму серверных схем
 * (`apps/api/src/modules/auth/schema.ts`) — она нужна только чтобы показать
 * ошибку без похода на сервер. Источник правды остаётся серверным:
 * стойкость пароля (zxcvbn) считается там и приходит ошибкой `weak_password`.
 */
export const PASSWORD_MIN_LENGTH = 10;

export function createAuthSchemas(messages: Dictionary['auth']['validation']) {
  const email = z.string().min(1, messages.emailRequired).email(messages.emailInvalid);
  const newPassword = z.string().min(PASSWORD_MIN_LENGTH, messages.passwordShort);

  return {
    login: z.object({
      email,
      password: z.string().min(1, messages.passwordRequired),
    }),
    /**
     * Clickwrap (docs/north-america-launch-requirements.md §6.4). Both
     * acknowledgements are `literal(true)` for the same reason as on the server:
     * an unticked box is a refusal to register, not a value the service decides
     * what to do with later.
     */
    register: z.object({
      name: z.string().trim().min(1, messages.nameRequired),
      email,
      password: newPassword,
      acceptedTerms: z.literal(true, {
        errorMap: () => ({ message: messages.termsRequired }),
      }),
      adultAck: z.literal(true, {
        errorMap: () => ({ message: messages.adultRequired }),
      }),
    }),
    forgot: z.object({ email }),
    reset: z.object({ password: newPassword }),
  };
}

export type AuthSchemas = ReturnType<typeof createAuthSchemas>;
export type LoginValues = z.infer<AuthSchemas['login']>;
export type RegisterValues = z.infer<AuthSchemas['register']>;
export type ForgotValues = z.infer<AuthSchemas['forgot']>;
export type ResetValues = z.infer<AuthSchemas['reset']>;
